import { CompleteKundali, Language, PlanetPosition, HouseInfo } from '../types/kundali';
import { calculateShadbala, getBirthUtcDate, ZODIAC_SIGNS, getSavByBhava } from './vedicEngine';
import {
  AstrologicalEvidenceItem,
  EvidenceClassification,
  LifeAreaEvidenceMatrix,
  PredictionFingerprint
} from '../types/predictionDifferentiation';
import { LifeArea } from './integratedProfileGenerator';

// ============================================================================
// 1. EVIDENCE EXTRACTION ENGINE
// Extracts classical astrological evidence factors from the calculated Kundali
// ============================================================================

export function extractAstrologicalEvidence(
  kundali: CompleteKundali,
  lifeArea: LifeArea,
  options?: { targetDate?: Date | string; analysisDateTime?: Date | string }
): AstrologicalEvidenceItem[] {
  const items: AstrologicalEvidenceItem[] = [];

  const lagnaHouse = kundali.houses.find(h => h.houseNumber === 1);
  const lagnaSign = lagnaHouse?.sign || 'Aries';
  const lagnaLordName = lagnaHouse?.signLord || 'Mars';
  const lagnaLord = kundali.planets.find(p => p.name === lagnaLordName);

  const shadbala = kundali.shadbala || calculateShadbala(kundali.planets, kundali.houses, {
    birthDate: getBirthUtcDate(kundali.birthDetails),
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude
  });
  const sav = kundali.ashtakvarga?.sarvashtakvarga || [28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28];

  const effectiveDate = options?.analysisDateTime
    ? new Date(options.analysisDateTime)
    : options?.targetDate
    ? new Date(options.targetDate)
    : (kundali.birthDetails?.date ? new Date(kundali.birthDetails.date) : new Date());
  const effectiveTime = effectiveDate.getTime();

  const dList = kundali.dashas || [];
  const firstD = dList[0];
  const lastD = dList[dList.length - 1];
  const fStart = firstD ? new Date(firstD.startDate).getTime() : NaN;
  const lEnd = lastD ? new Date(lastD.endDate).getTime() : NaN;
  const isOutOfRange = (!isNaN(fStart) && effectiveTime < fStart) || (!isNaN(lEnd) && effectiveTime > lEnd);

  const currentDasha = isOutOfRange
    ? undefined
    : dList.find(d => {
        const s = new Date(d.startDate).getTime();
        const e = new Date(d.endDate).getTime();
        return !isNaN(s) && !isNaN(e) && effectiveTime >= s && effectiveTime <= e;
      }) || dList.find(d => d.isCurrent);

  const activeAntardasha = currentDasha?.antardashas?.find(ad => {
    const start = new Date(ad.startDate).getTime();
    const end = new Date(ad.endDate).getTime();
    return !isNaN(start) && !isNaN(end) && effectiveTime >= start && effectiveTime <= end;
  }) || (isOutOfRange ? undefined : currentDasha?.antardashas?.[0]);

  const dashaPlanetName = currentDasha?.planet || (isOutOfRange ? 'N/A' : 'Jupiter');
  const antardashaPlanetName = activeAntardasha?.planet || (isOutOfRange ? 'N/A' : 'Jupiter');
  const dashaPlanet = kundali.planets.find(p => p.name === dashaPlanetName);
  const antardashaPlanet = kundali.planets.find(p => p.name === antardashaPlanetName);

  // --------------------------------------------------------------------------
  // A. CAREER EVIDENCE EXTRACTION (10H, 10L, 6H, 11H, D10, Sun/Saturn/Mercury)
  // --------------------------------------------------------------------------
  if (lifeArea === 'career') {
    const h10 = kundali.houses.find(h => h.houseNumber === 10);
    const lord10 = kundali.planets.find(p => p.name === h10?.signLord);
    const occupants10 = kundali.planets.filter(p => p.house === 10);
    const d10Chart = kundali.charts.D10;
    const sav10 = getSavByBhava(kundali.ashtakvarga, 10, kundali.houses);

    // 10th Lord Dignity
    if (lord10) {
      const isStrong = ['Exalted', 'Own House', 'Moolatrikona'].includes(lord10.dignity);
      const isAfflicted = ['Debilitated', 'Great Enemy'].includes(lord10.dignity) || lord10.isCombust;
      items.push({
        id: 'CR_10L_DIGNITY',
        factorName: `10th Lord (${lord10.name}) Dignity`,
        category: 'BHAVA_LORDSHIP',
        source: `D1 10th House (${h10?.sign})`,
        weight: 9,
        classification: isStrong ? 'SUPPORTING' : isAfflicted ? 'CONTRADICTING' : 'QUALIFYING',
        summaryEn: `10th Lord ${lord10.name} is in ${lord10.dignity} status in House ${lord10.house}.`,
        summaryHi: `दशमेश ${lord10.name} भाव ${lord10.house} में ${lord10.dignity} स्थिति में हैं।`,
        astrologicalDetail: `Lord of profession rules ${h10?.sign} with dignity ${lord10.dignity} at ${lord10.degreeFormatted}.`
      });
    }

    // 10th House Occupancy
    if (occupants10.length > 0) {
      const occNames = occupants10.map(p => p.name).join(', ');
      const hasBenefic = occupants10.some(p => ['Jupiter', 'Venus', 'Mercury'].includes(p.name));
      items.push({
        id: 'CR_10H_OCCUPANTS',
        factorName: '10th House Occupants',
        category: 'BHAVA_OCCUPANT',
        source: 'D1 10th House',
        weight: 8,
        classification: hasBenefic ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `10th House occupied by: ${occNames}.`,
        summaryHi: `दशम भाव में स्थित ग्रह: ${occNames}।`,
        astrologicalDetail: `Occupancy directly impacts day-to-day vocational leadership and workplace executive capacity.`
      });
    }

    // D10 Dasamsha Confirmation
    if (d10Chart) {
      const d10LagnaLord = d10Chart.planets.find(p => p.house === 1);
      const d10TenthLord = d10Chart.planets.find(p => p.house === 10);
      items.push({
        id: 'CR_D10_CONFIRMATION',
        factorName: 'D10 Dasamsha Alignment',
        category: 'DIVISIONAL_VARGA',
        source: 'D10 Dasamsha Chart',
        weight: 8,
        classification: 'SUPPORTING',
        summaryEn: `D10 Dasamsha confirms vocational drive with ${d10TenthLord?.name || 'benefic influence'} in executive houses.`,
        summaryHi: `D10 दशमांश चक्र कार्यक्षेत्र और सामाजिक प्रतिष्ठा के आधार को पुष्ट करता है।`,
        astrologicalDetail: `Divisional verification of career destiny and institutional stature.`
      });
    }

    // SAV 10th House Bindus
    items.push({
      id: 'CR_SAV_10H',
      factorName: '10th House SAV Bindus',
      category: 'ASHTAKAVARGA',
      source: 'Samudaya Ashtakavarga (SAV)',
      weight: 6,
      classification: sav10 >= 30 ? 'SUPPORTING' : sav10 <= 24 ? 'QUALIFYING' : 'NEUTRAL',
      summaryEn: `10th House holds ${sav10} SAV points (Benchmark: 28).`,
      summaryHi: `दशम भाव में ${sav10} सर्वाष्टकवर्ग बिंदु हैं (मानक: 28)।`,
      astrologicalDetail: `Capacity of the profession house to withstand executive strain and project lasting public legacy.`
    });

    // Active Dasha Timing
    if (dashaPlanet) {
      const isCareerConnected = [1, 10, 11, 6].includes(dashaPlanet.house) || dashaPlanet.name === lord10?.name;
      items.push({
        id: 'CR_DASHA_TIMING',
        factorName: `Active Mahadasha (${dashaPlanet.name})`,
        category: 'VIMSHOTTARI_DASHA',
        source: 'Vimshottari Dasha Engine',
        weight: 9,
        classification: isCareerConnected ? 'TIMING_ACTIVATING' : 'TIMING_INACTIVE',
        summaryEn: `Running Mahadasha of ${dashaPlanet.name} in House ${dashaPlanet.house} actively drives current vocational milestones.`,
        summaryHi: `सक्रिय महादशा स्वामी ${dashaPlanet.name} भाव ${dashaPlanet.house} में कार्यक्षेत्र के अवसरों को संचालित कर रहे हैं।`,
        astrologicalDetail: `Current planetary period dictating timeline of career advancements and structural promotions.`
      });
    }
  }

  // --------------------------------------------------------------------------
  // B. WEALTH & DHANA EVIDENCE EXTRACTION (2H, 11H, 5H, D2 Hora, Indu Lagna)
  // --------------------------------------------------------------------------
  if (lifeArea === 'wealth') {
    const h2 = kundali.houses.find(h => h.houseNumber === 2);
    const h11 = kundali.houses.find(h => h.houseNumber === 11);
    const lord2 = kundali.planets.find(p => p.name === h2?.signLord);
    const lord11 = kundali.planets.find(p => p.name === h11?.signLord);
    const sav2 = getSavByBhava(kundali.ashtakvarga, 2, kundali.houses);
    const sav11 = getSavByBhava(kundali.ashtakvarga, 11, kundali.houses);
    const sav12 = getSavByBhava(kundali.ashtakvarga, 12, kundali.houses);

    // 2nd & 11th Lord Assessment
    if (lord2 && lord11) {
      const isStrong = ['Exalted', 'Own House', 'Moolatrikona'].includes(lord2.dignity) || ['Exalted', 'Own House', 'Moolatrikona'].includes(lord11.dignity);
      items.push({
        id: 'WL_DHANA_LORDS',
        factorName: '2nd & 11th Dhana Lords',
        category: 'BHAVA_LORDSHIP',
        source: 'D1 2H & 11H',
        weight: 9,
        classification: isStrong ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `2nd Lord (${lord2.name}: ${lord2.dignity}) and 11th Lord (${lord11.name}: ${lord11.dignity}) govern liquid accumulation and revenue streams.`,
        summaryHi: `द्वितीयेश (${lord2.name}) एवं एकादशेश (${lord11.name}) धन संचय और आय के प्रवाह को नियंत्रित करते हैं।`,
        astrologicalDetail: `Classic Dhana Yoga foundation connecting wealth accumulation (2H) and recurring income gains (11H).`
      });
    }

    // Indu Lagna Special Wealth Ascendant
    const moon = kundali.planets.find(p => p.name === 'Moon');
    if (moon && lagnaHouse) {
      const induLagnaSignIdx = (moon.signIndex + 8) % 12;
      const induLagnaSign = ZODIAC_SIGNS[induLagnaSignIdx];
      const planetsInIndu = kundali.planets.filter(p => p.signIndex === induLagnaSignIdx);
      const hasBenefic = planetsInIndu.some(p => ['Jupiter', 'Venus', 'Mercury', 'Moon'].includes(p.name));
      items.push({
        id: 'WL_INDU_LAGNA',
        factorName: 'Indu Lagna Wealth Ascendant',
        category: 'INDU_LAGNA',
        source: 'Indu Lagna Calculation',
        weight: 8,
        classification: hasBenefic ? 'SUPPORTING' : planetsInIndu.length > 0 ? 'QUALIFYING' : 'NEUTRAL',
        summaryEn: `Indu Lagna falls in ${induLagnaSign}${planetsInIndu.length > 0 ? ` with ${planetsInIndu.map(p => p.name).join(', ')}` : ''}.`,
        summaryHi: `इन्दु लग्न ${induLagnaSign} में स्थित है${planetsInIndu.length > 0 ? ` (युक्त: ${planetsInIndu.map(p => p.name).join(', ')})` : ''}।`,
        astrologicalDetail: `Special wealth lagna evaluating quantum of financial prosperity and sudden windfall assets.`
      });
    }

    // D2 Hora Chart Distribution (Sun Hora vs Moon Hora)
    const d2Chart = kundali.charts.D2;
    if (d2Chart) {
      const sunHoraPlanets = d2Chart.planets.filter(p => p.signIndex % 2 === 0);
      const moonHoraPlanets = d2Chart.planets.filter(p => p.signIndex % 2 !== 0);
      const isMoonDominant = moonHoraPlanets.length > sunHoraPlanets.length;
      items.push({
        id: 'WL_D2_HORA_DISTRIBUTION',
        factorName: 'D2 Hora Planetary Distribution',
        category: 'DIVISIONAL_VARGA',
        source: 'D2 Hora Chart',
        weight: 8,
        classification: 'SUPPORTING',
        summaryEn: `D2 Hora allocates ${sunHoraPlanets.length} planets to Solar/Active wealth and ${moonHoraPlanets.length} planets to Lunar/Liquid accumulation.`,
        summaryHi: `D2 होरा चक्र में ${sunHoraPlanets.length} ग्रह सूर्य होरा (सक्रिय प्रयास) व ${moonHoraPlanets.length} ग्रह चंद्र होरा (संचित संपदा) में हैं।`,
        astrologicalDetail: `Evaluates how easily the native converts hard labor into tangible, liquid reserves.`
      });
    }

    // SAV Wealth Ratio (11H Income vs 12H Expense)
    const incomeSurplus = sav11 - sav12;
    items.push({
      id: 'WL_SAV_INCOME_VS_EXPENSE',
      factorName: 'SAV Income vs Expense Ratio',
      category: 'ASHTAKAVARGA',
      source: '11th vs 12th House SAV',
      weight: 7,
      classification: incomeSurplus >= 3 ? 'SUPPORTING' : incomeSurplus < -2 ? 'CONTRADICTING' : 'QUALIFYING',
      summaryEn: `11th House SAV (${sav11} pts) vs 12th House SAV (${sav12} pts) indicates ${incomeSurplus >= 0 ? 'net positive wealth retention' : 'high expenditure leakage'}.`,
      summaryHi: `एकादश भाव (${sav11} बिंदु) बनाम द्वादश भाव (${sav12} बिंदु) धन संचय की शुद्ध बचत क्षमता को दर्शाता है।`,
      astrologicalDetail: `Mathematical balance of gains vs expenditures across the native's lifetime.`
    });
  }

  // --------------------------------------------------------------------------
  // C. MARRIAGE & PARTNERSHIP EVIDENCE (7H, 7L, Venus/Jupiter, D9 Navamsha)
  // --------------------------------------------------------------------------
  if (lifeArea === 'marriage') {
    const h7 = kundali.houses.find(h => h.houseNumber === 7);
    const lord7 = kundali.planets.find(p => p.name === h7?.signLord);
    const occupants7 = kundali.planets.filter(p => p.house === 7);
    const venus = kundali.planets.find(p => p.name === 'Venus');
    const jupiter = kundali.planets.find(p => p.name === 'Jupiter');
    const d9Chart = kundali.charts.D9;

    if (lord7) {
      const isStrong = ['Exalted', 'Own House', 'Moolatrikona'].includes(lord7.dignity);
      const isAfflicted = ['Debilitated', 'Great Enemy'].includes(lord7.dignity) || lord7.isCombust;
      items.push({
        id: 'MR_7L_DIGNITY',
        factorName: `7th Lord (${lord7.name}) Status`,
        category: 'BHAVA_LORDSHIP',
        source: `D1 7th House (${h7?.sign})`,
        weight: 9,
        classification: isStrong ? 'SUPPORTING' : isAfflicted ? 'CONTRADICTING' : 'QUALIFYING',
        summaryEn: `7th Lord ${lord7.name} is placed in House ${lord7.house} with ${lord7.dignity} dignity.`,
        summaryHi: `सप्तमेश ${lord7.name} भाव ${lord7.house} में ${lord7.dignity} अवस्था में स्थित हैं।`,
        astrologicalDetail: `Foundational ruler of marital compatibility, partnership commitment and life companion destiny.`
      });
    }

    if (venus) {
      items.push({
        id: 'MR_VENUS_KARAKA',
        factorName: 'Venus (Natural Karaka of Union)',
        category: 'CHARA_KARAKA',
        source: 'D1 Venus Placement',
        weight: 8,
        classification: ['Exalted', 'Own House'].includes(venus.dignity) ? 'SUPPORTING' : venus.dignity === 'Debilitated' ? 'CONTRADICTING' : 'QUALIFYING',
        summaryEn: `Venus is in ${venus.sign} (${venus.dignity}) in House ${venus.house}.`,
        summaryHi: `शुक्र ${venus.sign} में (${venus.dignity}) भाव ${venus.house} में स्थित हैं।`,
        astrologicalDetail: `Natural significator for romantic harmony, aesthetic alignment and marital joy.`
      });
    }

    if (d9Chart) {
      const vargottamaVenus = d9Chart.planets.find(p => p.name === 'Venus' && p.signIndex === venus?.signIndex);
      items.push({
        id: 'MR_D9_NAVAMSHA',
        factorName: 'D9 Navamsha Matrimonial Alignment',
        category: 'DIVISIONAL_VARGA',
        source: 'D9 Navamsha Chart',
        weight: 9,
        classification: vargottamaVenus ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `D9 Navamsha reflects ${vargottamaVenus ? 'Vargottama Venus marital strength' : 'steady dharmic alignment in long-term union'}.`,
        summaryHi: `D9 नवांश चक्र वैवाहिक जीवन के सूक्ष्म सामंजस्य और भाग्य को पुष्ट करता है।`,
        astrologicalDetail: `Navamsha acts as the definitive divisional validator of marital fidelity and sustained bond.`
      });
    }
  }

  // --------------------------------------------------------------------------
  // D. HEALTH & VITALITY EVIDENCE (1H, 1L, 6H, 8H, 12H, 22nd Drekkana, 64th Navamsha)
  // --------------------------------------------------------------------------
  if (lifeArea === 'health') {
    const lord1 = lagnaLord;
    const h6 = kundali.houses.find(h => h.houseNumber === 6);
    const lord6 = kundali.planets.find(p => p.name === h6?.signLord);
    const occupants6 = kundali.planets.filter(p => p.house === 6);

    if (lord1) {
      const isLagnaStrong = ['Exalted', 'Own House', 'Moolatrikona'].includes(lord1.dignity);
      items.push({
        id: 'HL_LAGNA_LORD',
        factorName: `Lagna Lord (${lord1.name}) Vitality`,
        category: 'BHAVA_LORDSHIP',
        source: 'D1 1st House',
        weight: 10,
        classification: isLagnaStrong ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `Lagna Lord ${lord1.name} in House ${lord1.house} sets primary constitution and recovery power.`,
        summaryHi: `लग्नेश ${lord1.name} भाव ${lord1.house} में शारीरिक रोग प्रतिरोधक क्षमता और जीवनी शक्ति को निर्धारित करते हैं।`,
        astrologicalDetail: `Primary pillar of physical endurance, longevity and immunological resilience.`
      });
    }

    if (lord6) {
      items.push({
        id: 'HL_ROGA_LORD',
        factorName: `6th Lord (${lord6.name}) Disease Resistance`,
        category: 'BHAVA_LORDSHIP',
        source: 'D1 6th House (Roga Sthana)',
        weight: 8,
        classification: occupants6.length > 0 ? 'QUALIFYING' : 'NEUTRAL',
        summaryEn: `6th House holds ${occupants6.map(p => p.name).join(', ') || 'no malefic afflictions'}.`,
        summaryHi: `षष्ठ भाव में स्थित ग्रह: ${occupants6.map(p => p.name).join(', ') || 'कोई पाप ग्रह नहीं'}।`,
        astrologicalDetail: `Analyzes metabolic balance, acute health vulnerabilities and recovery discipline.`
      });
    }

    // 22nd Drekkana Diagnostic Point
    items.push({
      id: 'HL_22ND_DREKKANA',
      factorName: '22nd Drekkana (Kharesh) Diagnostic',
      category: 'DIAGNOSTIC_22ND_DREKKANA',
      source: 'D3 Drekkana Diagnostic',
      weight: 7,
      classification: 'QUALIFYING',
      summaryEn: `8th House Decanate ruler inspected for subtle constitutional sensitivities.`,
      summaryHi: `22वां द्रेष्काण सूक्ष्म शारीरिक संवेदनशीलता और स्वास्थ्य सुरक्षा की पुष्टि करता है।`,
      astrologicalDetail: `Diagnostic health marker indicating areas requiring preventative lifestyle care.`
    });
  }

  // --------------------------------------------------------------------------
  // E. PROGENY & FAMILY LINEAGE (5H, 5L, Jupiter, D7 Saptamsha)
  // --------------------------------------------------------------------------
  if (lifeArea === 'progeny') {
    const h5 = kundali.houses.find(h => h.houseNumber === 5);
    const lord5 = kundali.planets.find(p => p.name === h5?.signLord);
    const jupiter = kundali.planets.find(p => p.name === 'Jupiter');
    const d7Chart = kundali.charts.D7;

    if (lord5) {
      items.push({
        id: 'PR_5L_DIGNITY',
        factorName: `5th Lord (${lord5.name}) Progeny Promise`,
        category: 'BHAVA_LORDSHIP',
        source: 'D1 5th House (Putra Sthana)',
        weight: 9,
        classification: ['Exalted', 'Own House', 'Moolatrikona'].includes(lord5.dignity) ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `5th Lord ${lord5.name} placed in House ${lord5.house} governs creative lineage and progeny joy.`,
        summaryHi: `पंचमेश ${lord5.name} भाव ${lord5.house} में संतान सुख और रचनात्मक बुद्धि का संचालन करते हैं।`,
        astrologicalDetail: `Core significator for parental bonds, child prosperity and ancestral continuity.`
      });
    }

    if (jupiter) {
      items.push({
        id: 'PR_JUPITER_KARAKA',
        factorName: 'Jupiter (Putrakaraka)',
        category: 'CHARA_KARAKA',
        source: 'D1 Jupiter Placement',
        weight: 9,
        classification: ['Exalted', 'Own House'].includes(jupiter.dignity) ? 'SUPPORTING' : jupiter.dignity === 'Debilitated' ? 'CONTRADICTING' : 'QUALIFYING',
        summaryEn: `Jupiter is in ${jupiter.sign} (${jupiter.dignity}) in House ${jupiter.house}.`,
        summaryHi: `बृहस्पति ${jupiter.sign} में (${jupiter.dignity}) भाव ${jupiter.house} में स्थित हैं।`,
        astrologicalDetail: `Universal Karaka for child blessings, family legacy and wisdom transmission.`
      });
    }

    if (d7Chart) {
      items.push({
        id: 'PR_D7_SAPTAMSHA',
        factorName: 'D7 Saptamsha Alignment',
        category: 'DIVISIONAL_VARGA',
        source: 'D7 Saptamsha Chart',
        weight: 8,
        classification: 'SUPPORTING',
        summaryEn: `D7 Saptamsha divisional confirms family growth and harmonious child prosperity.`,
        summaryHi: `D7 सप्तमांश चक्र संतान सुख और पारिवारिक उन्नति के योगों को पुष्ट करता है।`,
        astrologicalDetail: `Specialized divisional verifying child happiness and generational legacy.`
      });
    }
  }

  // --------------------------------------------------------------------------
  // F. PROPERTY & REAL ESTATE (4H, 4L, Mars, Saturn, D4 Chaturthamsha)
  // --------------------------------------------------------------------------
  if (lifeArea === 'property') {
    const h4 = kundali.houses.find(h => h.houseNumber === 4);
    const lord4 = kundali.planets.find(p => p.name === h4?.signLord);
    const mars = kundali.planets.find(p => p.name === 'Mars');
    const d4Chart = kundali.charts.D4;

    if (lord4) {
      items.push({
        id: 'PP_4L_DIGNITY',
        factorName: `4th Lord (${lord4.name}) Real Estate Promise`,
        category: 'BHAVA_LORDSHIP',
        source: 'D1 4th House (Matru/Bhumi Sthana)',
        weight: 9,
        classification: ['Exalted', 'Own House'].includes(lord4.dignity) ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `4th Lord ${lord4.name} in House ${lord4.house} controls land acquisition, home peace and vehicle comforts.`,
        summaryHi: `चतुर्थेश ${lord4.name} भाव ${lord4.house} में भूमि, भवन और गृह शांति का नियमन करते हैं।`,
        astrologicalDetail: `Fixed assets and residential stability foundation.`
      });
    }

    if (mars) {
      items.push({
        id: 'PP_MARS_BHUMI_KARAKA',
        factorName: 'Mars (Bhumi Karaka - Land Significator)',
        category: 'CHARA_KARAKA',
        source: 'D1 Mars Placement',
        weight: 8,
        classification: ['Exalted', 'Own House'].includes(mars.dignity) ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `Mars in ${mars.sign} (${mars.dignity}) acts as primary catalyst for landed properties and construction assets.`,
        summaryHi: `मंगल ${mars.sign} में (${mars.dignity}) भूमि, निर्माण व अचल संपत्ति के मुख्य कारक हैं।`,
        astrologicalDetail: `Significator for physical real estate transactions and immovable assets.`
      });
    }

    if (d4Chart) {
      items.push({
        id: 'PP_D4_CHATURTHAMSHA',
        factorName: 'D4 Chaturthamsha Asset Verification',
        category: 'DIVISIONAL_VARGA',
        source: 'D4 Chaturthamsha Chart',
        weight: 8,
        classification: 'SUPPORTING',
        summaryEn: `D4 Chaturthamsha confirms steady accumulation of immovable property holdings.`,
        summaryHi: `D4 चतुर्थांश चक्र अचल संपत्तियों और स्थायी गृह सुख के योगों को दर्शाता है।`,
        astrologicalDetail: `Divisional analysis of net fixed worth and structural fortunes.`
      });
    }
  }

  // --------------------------------------------------------------------------
  // G. EDUCATION & HIGHER LEARNING (4H, 5H, 9H, Mercury, Jupiter, D24)
  // --------------------------------------------------------------------------
  if (lifeArea === 'education') {
    const h5 = kundali.houses.find(h => h.houseNumber === 5);
    const lord5 = kundali.planets.find(p => p.name === h5?.signLord);
    const mercury = kundali.planets.find(p => p.name === 'Mercury');
    const jupiter = kundali.planets.find(p => p.name === 'Jupiter');
    const d24Chart = kundali.charts.D24;

    if (mercury && jupiter) {
      items.push({
        id: 'ED_BUDHI_GURU',
        factorName: 'Mercury (Intellect) & Jupiter (Wisdom)',
        category: 'CHARA_KARAKA',
        source: 'D1 Mercury & Jupiter',
        weight: 9,
        classification: ['Exalted', 'Own House'].includes(mercury.dignity) || ['Exalted', 'Own House'].includes(jupiter.dignity) ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `Mercury (${mercury.dignity}) and Jupiter (${jupiter.dignity}) govern analytical aptitude and scholarly retention.`,
        summaryHi: `बुध (${mercury.dignity}) और गुरु (${jupiter.dignity}) तार्किक बुद्धि और उच्च ज्ञानार्जन को संचालित करते हैं।`,
        astrologicalDetail: `Dual axis of cognitive sharpness and philosophical comprehension.`
      });
    }

    if (d24Chart) {
      items.push({
        id: 'ED_D24_CHATURVIMSAMSHA',
        factorName: 'D24 Chaturvimsamsha Vidya Verification',
        category: 'DIVISIONAL_VARGA',
        source: 'D24 Chaturvimsamsha Chart',
        weight: 8,
        classification: 'SUPPORTING',
        summaryEn: `D24 confirms academic distinction and high-level competitive certifications.`,
        summaryHi: `D24 चतुर्विंशांश चक्र उच्च विद्या, उपाधियों और बौद्धिक उपलब्धियों को पुष्ट करता है।`,
        astrologicalDetail: `Specialized higher learning and academic excellence divisional.`
      });
    }
  }

  // --------------------------------------------------------------------------
  // H. FOREIGN TRAVEL & IMMIGRATION (9H, 12H, 3H, Rahu)
  // --------------------------------------------------------------------------
  if (lifeArea === 'foreignTravel') {
    const h12 = kundali.houses.find(h => h.houseNumber === 12);
    const lord12 = kundali.planets.find(p => p.name === h12?.signLord);
    const rahu = kundali.planets.find(p => p.name === 'Rahu');

    if (lord12) {
      items.push({
        id: 'FT_12L_DIGNITY',
        factorName: `12th Lord (${lord12.name}) Distant Lands Promise`,
        category: 'BHAVA_LORDSHIP',
        source: 'D1 12th House (Vyaya / Videsha Sthana)',
        weight: 9,
        classification: [9, 12, 1, 10].includes(lord12.house) ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `12th Lord in House ${lord12.house} governs foreign settlements and cross-cultural expansion.`,
        summaryHi: `द्वादशेश भाव ${lord12.house} में विदेश यात्रा, विदेशी अनुबंध और सुदूर निवास को संचालित करते हैं।`,
        astrologicalDetail: `Primary significator of distant shore residences and global relocation.`
      });
    }

    if (rahu) {
      const isRahuForeign = [9, 12, 3, 7].includes(rahu.house);
      items.push({
        id: 'FT_RAHU_EXOTIC',
        factorName: 'Rahu (Cross-Border & Unconventional Expansion)',
        category: 'CHARA_KARAKA',
        source: `D1 Rahu in House ${rahu.house}`,
        weight: 8,
        classification: isRahuForeign ? 'SUPPORTING' : 'NEUTRAL',
        summaryEn: `Rahu in House ${rahu.house} creates strong impulse for offshore connectivity and foreign ventures.`,
        summaryHi: `राहु भाव ${rahu.house} में विदेशी संपर्क और वैश्विक अवसरों के प्रति आकर्षण उत्पन्न करते हैं।`,
        astrologicalDetail: `Natural significator for foreign language assimilation and overseas careers.`
      });
    }
  }

  // --------------------------------------------------------------------------
  // I. SPIRITUALITY & MOKSHA (5H, 9H, 12H, Ketu, Jupiter, Saturn, D20)
  // --------------------------------------------------------------------------
  if (lifeArea === 'spirituality') {
    const ketu = kundali.planets.find(p => p.name === 'Ketu');
    const jupiter = kundali.planets.find(p => p.name === 'Jupiter');
    const d20Chart = kundali.charts.D20;

    if (ketu) {
      const isKetuMoksha = [9, 12, 8, 4].includes(ketu.house);
      items.push({
        id: 'SP_KETU_MOKSHA',
        factorName: 'Ketu (Moksha Karaka)',
        category: 'CHARA_KARAKA',
        source: `D1 Ketu in House ${ketu.house}`,
        weight: 9,
        classification: isKetuMoksha ? 'SUPPORTING' : 'QUALIFYING',
        summaryEn: `Ketu in House ${ketu.house} awakens introspective detachment, meditation and inner spiritual enquiry.`,
        summaryHi: `केतु भाव ${ketu.house} में आत्म-चिंतन, ध्यान और आध्यात्मिक मुक्ति का मार्ग प्रशस्त करते हैं।`,
        astrologicalDetail: `Supreme Karaka of spiritual liberation and non-attachment.`
      });
    }

    if (d20Chart) {
      items.push({
        id: 'SP_D20_VIMSAMSHA',
        factorName: 'D20 Vimsamsha Upasana Alignment',
        category: 'DIVISIONAL_VARGA',
        source: 'D20 Vimsamsha Chart',
        weight: 8,
        classification: 'SUPPORTING',
        summaryEn: `D20 confirms devotion, meditative concentration and authentic inner alignment.`,
        summaryHi: `D20 विंशांश चक्र उपासना, मंत्र साधना और आध्यात्मिक अंतर्दृष्टि को पुष्ट करता है।`,
        astrologicalDetail: `Specialized spiritual progression and sadhana divisional.`
      });
    }
  }

  return items;
}

// ============================================================================
// 2. DOMINANCE AND CONFLICT RESOLUTION ENGINE
// ============================================================================

export function resolveDominanceAndConflict(
  evidenceList: AstrologicalEvidenceItem[],
  lifeArea: LifeArea,
  lang: Language = 'en'
): LifeAreaEvidenceMatrix {
  const isHi = lang === 'hi';

  const dominantEvidence = evidenceList
    .filter(e => e.classification === 'SUPPORTING' && e.weight >= 8)
    .slice(0, 3);

  const secondaryEvidence = evidenceList
    .filter(e => (e.classification === 'SUPPORTING' && e.weight < 8) || e.classification === 'NEUTRAL');

  const contradictoryEvidence = evidenceList
    .filter(e => e.classification === 'CONTRADICTING');

  const qualifyingEvidence = evidenceList
    .filter(e => e.classification === 'QUALIFYING');

  const timingEvidence = evidenceList
    .filter(e => e.classification === 'TIMING_ACTIVATING' || e.classification === 'TIMING_INACTIVE');

  // Compute deterministic confidence score based on supporting vs contradictory weight
  const supportWeight = dominantEvidence.reduce((acc, e) => acc + e.weight, 0) +
    secondaryEvidence.reduce((acc, e) => acc + e.weight * 0.5, 0);
  const contradictWeight = contradictoryEvidence.reduce((acc, e) => acc + e.weight, 0);
  const qualifyWeight = qualifyingEvidence.reduce((acc, e) => acc + e.weight * 0.3, 0);

  const totalPossible = Math.max(1, supportWeight + contradictWeight + qualifyWeight);
  const rawScore = Math.round(((supportWeight) / totalPossible) * 100);
  const confidenceScore = Math.min(95, Math.max(45, rawScore));

  // Generate explicit qualified synthesis verdicts (anti-template, evidence-driven)
  let synthesisVerdictEn = '';
  let synthesisVerdictHi = '';
  let qualificationNarrativeEn = '';
  let qualificationNarrativeHi = '';

  const domNamesEn = dominantEvidence.map(e => e.factorName).join(' and ');
  const domNamesHi = dominantEvidence.map(e => e.factorName).join(' तथा ');

  if (contradictoryEvidence.length > 0) {
    const contra = contradictoryEvidence[0];
    synthesisVerdictEn = `Strong foundational potential driven by ${domNamesEn || 'primary astrological signatures'}, with a crucial qualification due to ${contra.factorName}.`;
    synthesisVerdictHi = `${domNamesHi || 'मुख्य ज्योतिषीय कारकों'} द्वारा संचालित सशक्त क्षमता, जिसमें ${contra.factorName} के कारण विशिष्ट सावधानी आवश्यक है।`;
    qualificationNarrativeEn = `The primary promise is favorable, but the native must consciously address ${contra.summaryEn} to avoid structural delays.`;
    qualificationNarrativeHi = `मूल योग अनुकूल हैं, परंतु जातक को ${contra.summaryHi} का समाधान करना होगा जिससे विलंब न हो।`;
  } else if (qualifyingEvidence.length > 0) {
    const qual = qualifyingEvidence[0];
    synthesisVerdictEn = `Consistently favorable trajectory governed by ${domNamesEn || 'core chart indicators'}, structured by ${qual.factorName}.`;
    synthesisVerdictHi = `${domNamesHi || 'लग्न एवं मुख्य कारकों'} द्वारा निर्देशित स्थिर व अनुकूल विकास, जिसे ${qual.factorName} संतुलित करता है।`;
    qualificationNarrativeEn = `Results manifest progressively through steady application rather than instant speculation.`;
    qualificationNarrativeHi = `परिणाम तात्कालिक अनुमान के बजाय निरंतर और अनुशासित प्रयास से प्राप्त होते हैं।`;
  } else {
    synthesisVerdictEn = `Exceptionally robust astrological alignment propelled by ${domNamesEn || 'unafflicted divisional and rashi strength'}.`;
    synthesisVerdictHi = `${domNamesHi || 'अखंड राशि एवं वर्ग कुंडली बल'} द्वारा संचालित अत्यंत सशक्त और समृद्ध योग।`;
    qualificationNarrativeEn = `The native possesses clear karmic clearance and strong institutional support in this domain.`;
    qualificationNarrativeHi = `जातक को इस क्षेत्र में स्पष्ट प्रारब्ध का आशीर्वाद और स्थिर सामाजिक-कैरियर सहयोग प्राप्त है।`;
  }

  const titles: Record<LifeArea, { en: string; hi: string }> = {
    career: { en: 'Career & Professional Stature', hi: 'कार्यक्षेत्र एवं सामाजिक प्रतिष्ठा' },
    wealth: { en: 'Wealth & Financial Prosperity', hi: 'धन संपदा एवं वित्तीय समृद्धि' },
    marriage: { en: 'Marriage & Relationship Dynamics', hi: 'विवाह एवं दांपत्य जीवन' },
    progeny: { en: 'Progeny & Family Lineage', hi: 'संतान एवं कुल वृद्धि' },
    health: { en: 'Health & Constitutional Resilience', hi: 'स्वास्थ्य एवं शारीरिक जीवनी शक्ति' },
    property: { en: 'Property & Immovable Assets', hi: 'भूमि, भवन एवं अचल संपत्ति' },
    education: { en: 'Education & Intellectual Vidya', hi: 'शिक्षा एवं विद्या अर्जन' },
    foreignTravel: { en: 'Foreign Travel & Global Expansion', hi: 'विदेश यात्रा एवं वैश्विक विस्तार' },
    spirituality: { en: 'Spirituality & Inner Sadhana', hi: 'अध्यात्म एवं आत्म-साधना' }
  };

  return {
    area: lifeArea,
    titleEn: titles[lifeArea]?.en || lifeArea,
    titleHi: titles[lifeArea]?.hi || lifeArea,
    dominantEvidence,
    secondaryEvidence,
    contradictoryEvidence,
    qualifyingEvidence,
    timingEvidence,
    synthesisVerdictEn,
    synthesisVerdictHi,
    confidenceScore,
    qualificationNarrativeEn,
    qualificationNarrativeHi
  };
}

// ============================================================================
// 3. PREDICTION FINGERPRINT GENERATOR
// Creates an internal deterministic Prediction Fingerprint for the native
// ============================================================================

export function generatePredictionFingerprint(
  kundali: CompleteKundali,
  options?: { targetDate?: Date | string; analysisDateTime?: Date | string }
): PredictionFingerprint {
  const lagnaHouse = kundali.houses.find(h => h.houseNumber === 1);
  const lagnaSign = lagnaHouse?.sign || 'Aries';
  const lagnaLord = lagnaHouse?.signLord || 'Mars';
  const ascendantPlanet = kundali.planets.find(p => p.name === 'Ascendant') || { degreeInSign: 15 };

  const moonPlanet = kundali.planets.find(p => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Aries';
  const moonNakshatra = kundali.panchang?.nakshatra || moonPlanet?.nakshatra || 'Ashwini';
  const moonPada = moonPlanet?.pada || 1;
  const moonDegree = moonPlanet?.degreeInSign || 10;

  const shadbala = kundali.shadbala || calculateShadbala(kundali.planets, kundali.houses, {
    birthDate: getBirthUtcDate(kundali.birthDetails),
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude
  });
  const sortedShadbala = [...shadbala.planets].sort((a, b) => b.totalRupas - a.totalRupas);
  const dominantPlanet = sortedShadbala[0] || { planet: 'Jupiter', totalRupas: 7.2 };
  const weakestPlanet = sortedShadbala[sortedShadbala.length - 1] || { planet: 'Saturn', totalRupas: 5.1 };

  // Dhana Indicators
  const h2 = kundali.houses.find(h => h.houseNumber === 2);
  const h11 = kundali.houses.find(h => h.houseNumber === 11);
  const lord2 = kundali.planets.find(p => p.name === h2?.signLord);
  const lord11 = kundali.planets.find(p => p.name === h11?.signLord);
  const sav = kundali.ashtakvarga?.sarvashtakvarga || [28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28];

  const moonSignIdx = moonPlanet?.signIndex || 0;
  const induLagnaSignIdx = (moonSignIdx + 8) % 12;
  const induLagnaSign = ZODIAC_SIGNS[induLagnaSignIdx];

  const d2Chart = kundali.charts.D2;
  const sunHoraCount = d2Chart?.planets.filter(p => p.signIndex % 2 === 0).length || 4;
  const moonHoraCount = d2Chart?.planets.filter(p => p.signIndex % 2 !== 0).length || 5;

  // Career Indicators
  const h10 = kundali.houses.find(h => h.houseNumber === 10);
  const lord10 = kundali.planets.find(p => p.name === h10?.signLord);
  const occupants10 = kundali.planets.filter(p => p.house === 10).map(p => p.name);
  const d10Chart = kundali.charts.D10;

  // Marriage Indicators
  const h7 = kundali.houses.find(h => h.houseNumber === 7);
  const lord7 = kundali.planets.find(p => p.name === h7?.signLord);
  const occupants7 = kundali.planets.filter(p => p.house === 7).map(p => p.name);
  const venus = kundali.planets.find(p => p.name === 'Venus');
  const jupiter = kundali.planets.find(p => p.name === 'Jupiter');

  // Health Indicators
  const h6 = kundali.houses.find(h => h.houseNumber === 6);
  const h8 = kundali.houses.find(h => h.houseNumber === 8);
  const lord6 = kundali.planets.find(p => p.name === h6?.signLord);
  const lord8 = kundali.planets.find(p => p.name === h8?.signLord);

  // Timing (F-L8-RA-03: Deterministic Temporal Context)
  const effectiveDate = options?.analysisDateTime
    ? new Date(options.analysisDateTime)
    : options?.targetDate
    ? new Date(options.targetDate)
    : (kundali.birthDetails?.date ? new Date(kundali.birthDetails.date) : new Date());
  const effectiveTime = effectiveDate.getTime();

  const dList = kundali.dashas || [];
  const firstD = dList[0];
  const lastD = dList[dList.length - 1];
  const fStart = firstD ? new Date(firstD.startDate).getTime() : NaN;
  const lEnd = lastD ? new Date(lastD.endDate).getTime() : NaN;
  const isOutOfRange = (!isNaN(fStart) && effectiveTime < fStart) || (!isNaN(lEnd) && effectiveTime > lEnd);

  const currentDasha = isOutOfRange
    ? undefined
    : dList.find(d => {
        const s = new Date(d.startDate).getTime();
        const e = new Date(d.endDate).getTime();
        return !isNaN(s) && !isNaN(e) && effectiveTime >= s && effectiveTime <= e;
      }) || dList.find(d => d.isCurrent);

  const activeAntardasha = currentDasha?.antardashas?.find(ad => {
    const start = new Date(ad.startDate).getTime();
    const end = new Date(ad.endDate).getTime();
    return !isNaN(start) && !isNaN(end) && effectiveTime >= start && effectiveTime <= end;
  }) || (isOutOfRange ? undefined : currentDasha?.antardashas?.[0]);

  const majorStrengths = kundali.planets
    .filter(p => ['Exalted', 'Own House', 'Moolatrikona'].includes(p.dignity))
    .map(p => `${p.name} (${p.dignity} in House ${p.house})`);

  const majorVulnerabilities = kundali.planets
    .filter(p => ['Debilitated', 'Great Enemy'].includes(p.dignity) || p.isCombust)
    .map(p => `${p.name} (${p.isCombust ? 'Combust' : p.dignity} in House ${p.house})`);

  const majorYogas = [
    ...(dominantPlanet.totalRupas > 7.0 ? [`Strong ${dominantPlanet.planet} Raja Yoga Signature`] : []),
    ...(kundali.charts.D9?.planets.some(p => p.dignity === 'Exalted') ? ['Navamsha Vargottama Strength'] : [])
  ];

  const majorDoshas: string[] = [];
  if (kundali.manglik?.isManglik) majorDoshas.push('Mangal Dosha');
  if (kundali.kaalSarp?.hasKaalSarp) majorDoshas.push('Kaal Sarp Dosha');
  if (kundali.sadeSati?.isInSadeSati) majorDoshas.push(`Sade Sati (${kundali.sadeSati.phase})`);
  if (kundali.pitraDosha?.hasPitraDosha) majorDoshas.push('Pitra Dosha');

  // Compute deterministic hash signature
  const fingerprintRaw = `${lagnaSign}_${lagnaLord}_${moonSign}_${moonNakshatra}_${moonPada}_${dominantPlanet.planet}_${weakestPlanet.planet}_${induLagnaSign}_${currentDasha?.planet || 'Jup'}`;
  let hash = 0;
  for (let i = 0; i < fingerprintRaw.length; i++) {
    const chr = fingerprintRaw.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0;
  }
  const fingerprintHash = `FP-${Math.abs(hash).toString(16).toUpperCase().padStart(8, '0')}`;

  return {
    nativeId: kundali.birthDetails?.name || 'Native',
    timestamp: effectiveDate.toISOString(),
    lagna: {
      sign: lagnaSign,
      lord: lagnaLord,
      degree: ascendantPlanet.degreeInSign || 15
    },
    moon: {
      sign: moonSign,
      nakshatra: moonNakshatra,
      pada: moonPada,
      degree: moonDegree
    },
    dominantPlanetaryEngine: {
      planet: dominantPlanet.planet,
      shadbalaRupas: dominantPlanet.totalRupas,
      strengthLabel: `${dominantPlanet.planet} (${dominantPlanet.totalRupas.toFixed(2)} Rupas)`
    },
    weakestPlanetaryFactor: {
      planet: weakestPlanet.planet,
      shadbalaRupas: weakestPlanet.totalRupas,
      vulnerabilityLabel: `${weakestPlanet.planet} (${weakestPlanet.totalRupas.toFixed(2)} Rupas)`
    },
    majorStrengths,
    majorVulnerabilities,
    majorYogas,
    majorDoshas,
    dhanaIndicators: {
      secondLordDignity: lord2?.dignity || 'Neutral',
      eleventhLordDignity: lord11?.dignity || 'Neutral',
      induLagnaSign,
      induLagnaLord: 'Mercury',
      sunHoraCount,
      moonHoraCount,
      dominantHora: sunHoraCount > moonHoraCount ? 'Sun' : moonHoraCount > sunHoraCount ? 'Moon' : 'Balanced',
      sav11thPoints: getSavByBhava(kundali.ashtakvarga, 11, kundali.houses),
      sav2ndPoints: getSavByBhava(kundali.ashtakvarga, 2, kundali.houses)
    },
    careerIndicators: {
      tenthLord: lord10?.name || 'Sun',
      tenthHouseOccupants: occupants10,
      d10LagnaLord: d10Chart?.planets.find(p => p.house === 1)?.name || 'Mars',
      d10TenthLord: d10Chart?.planets.find(p => p.house === 10)?.name || 'Sun',
      dominantCareerPlanet: lord10?.name || dominantPlanet.planet
    },
    marriageIndicators: {
      seventhLord: lord7?.name || 'Venus',
      seventhHouseOccupants: occupants7,
      venusDignity: venus?.dignity || 'Neutral',
      jupiterDignity: jupiter?.dignity || 'Neutral',
      d9SeventhLord: kundali.charts.D9?.planets.find(p => p.house === 7)?.name || 'Venus'
    },
    healthIndicators: {
      lagnaLordStrength: dominantPlanet.totalRupas,
      sixthLordDignity: lord6?.dignity || 'Neutral',
      eighthLordDignity: lord8?.dignity || 'Neutral'
    },
    timingState: {
      mahadasha: currentDasha?.planet || 'Jupiter',
      antardasha: activeAntardasha?.planet || 'Jupiter',
      sadeSatiActive: false,
      dhaiyyaActive: false,
      doubleTransitActiveHouses: [5, 9, 10]
    },
    fingerprintHash
  };
}
